<template>
  <el-card
    shadow="hover"
    :body-style="{ padding: '0' }"
    @mouseover="toggle(true)"
    @mouseleave="toggle(false)"
  >
    <template #header>
      <div class="flex-container around wrap">
        <el-tag v-if="deal.bought" size="small" type="success">
          Bought! Awaiting review
        </el-tag>
        <template v-else>
          <el-tag size="small">
            <span>{{ dealTypeLabel }}</span>
            <el-tooltip
              v-if="deal.traffic_delivery_warning"
              :content="deal.traffic_delivery_warning"
              effect="dark"
              placement="top"
            >
              <el-icon class="price-warning-icon">
                <InfoFilled />
              </el-icon>
            </el-tooltip>
          </el-tag>
          <PeriodLabel :from="deal.start" :to="deal.stop" />
        </template>
      </div>
    </template>
    <VueFlip :active-hover="!deal.bought" class="flip-flop-body">
      <template #front>
        <div class="front-container mb-1">
          <div v-if="deal.logo" class="logo-container">
            <img class="logo" :src="deal.logo" alt="logo" />
          </div>
          <el-tag
            :class="[{ center: !deal.logo }, 'website-tag']"
            type="success"
          >
            {{ deal.website }}
          </el-tag>
        </div>
      </template>
      <template #back>
        <div v-if="!deal.bought" class="flex-container around mt-3">
          <router-link :to="`/direct_deals/${deal.campaign_id}/buy`">
            <el-button type="success" round size="default"> Buy </el-button>
          </router-link>
          <router-link :to="`/direct_deals/${deal.campaign_id}`">
            <el-button type="primary" round size="default"> Details </el-button>
          </router-link>
        </div>
      </template>
    </VueFlip>
    <div class="footer">
      <div class="flex-container center brake">
        <AdTypeLabel :type="deal.type_id" />
        <BannerSize v-if="AdType.Banner === deal.type_id" :size="deal.size" />
      </div>
      <div class="flex-container center">
        <StreamIcon class="stream-icon" :stream-id="deal.stream" />
        <CountriesIcon
          :country-codes="deal.country_codes"
          style="margin-left: 5px"
        />
        <DevicesIcon :device-ids="deal.device_ids" style="margin-left: 5px" />
      </div>
    </div>
  </el-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import { VueFlip } from 'vue-flip'
import {
  AdType,
  FlatDealTypeLabels,
  FlatDealTypeLabel,
  FlatDealType
} from '@/api/types'

import StreamIcon from '@/components/StreamIcon.vue'
import BannerSize from '@/apps/traforama/components/BannerSize.vue'
import AdTypeLabel from '@/apps/traforama/components/AdTypeLabel.vue'
import CountriesIcon from '@/apps/traforama/components/CountriesIcon.vue'
import DevicesIcon from '@/apps/traforama/components/DevicesIcon.vue'
import PeriodLabel from './PeriodLabel.vue'
import { InfoFilled } from '@element-plus/icons-vue'

export default defineComponent({
  name: 'FlatDealCard',
  components: {
    VueFlip,
    StreamIcon,
    BannerSize,
    PeriodLabel,
    CountriesIcon,
    DevicesIcon,
    AdTypeLabel,
    InfoFilled
  },
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  setup(props, _context) {
    const active = ref(false)

    const deal = computed(() => props.item)

    const toggle = (value: boolean) => {
      active.value = value
    }

    const dealTypeLabel = computed(() => {
      const typeLabel = FlatDealTypeLabels.find(
        (x: FlatDealTypeLabel) => x.type_id === props.item.deal_type
      )
      if (props.item.deal_type === FlatDealType.CpmRate) {
        return `${typeLabel?.label}: ${props.item.flat_rate}$`
      } else {
        return `${typeLabel?.label}:  ${props.item.daily_rate} $/day`
      }
    })

    return { toggle, active, AdType, dealTypeLabel, deal }
  }
})
</script>

<style lang="scss" scoped>
:deep(.el-card__header) {
  padding: 10px 10px;
}

.front-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-container {
  height: 70%;
  align-items: center;
  display: flex;
  justify-content: center;
}

.logo {
  width: 100%;
  max-height: 100%;
  object-position: center;
  object-fit: cover;
}

.flex-container {
  display: flex;
  justify-content: space-between;

  &.center {
    justify-content: center;
  }

  &.around {
    justify-content: space-around;
  }

  &.wrap {
    flex-wrap: wrap;
  }
}

.website-tag {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
  margin-top: 5px;

  &.center {
    margin: 0;
    position: absolute;
    width: 100%;
    top: 50%;
    transform: translateY(-50%);
  }
}

.footer {
  padding: 10px 5px 0 5px;
  border-top: 1px solid #e6ebf5;
}

.price-warning-icon {
  margin-left: 4px;
  vertical-align: middle;
  color: #e6a23c;
}

.brake {
  margin-bottom: 5px;
}

.flip-flop-body {
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.el-form {
  margin: 0 20px;
}

.el-card {
  padding-bottom: 5px;
  margin-top: 15px;

  &:hover {
    cursor: pointer;
  }

  &.disabled {
    opacity: 0.4;
  }
}

.red {
  color: red;
}
</style>
